import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Layer, Map, MapRef, Source } from "react-map-gl";
import maplibregl from "maplibre-gl";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";
import "intersection-observer";
import "maplibre-gl/dist/maplibre-gl.css";
import "../styles/app.scss";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { LINKS_TO_CONTENT } from "src/pages/Homepage";

/**
 * This is a public access token connected to THE CITY's MapBox account:
 */
export const MAPBOX_TOKEN =
  "pk.eyJ1IjoidGhlLWNpdHkiLCJhIjoiY2xhMWVsNDY3MDJoYTNya2ptYWZpZW15dyJ9.iv4eTGq5GylMTUcYH16Big";

/**
 * This is a link to our custom MapBox Studio basemap style:
 */
export const MAPBOX_STYLE_URL =
  "mapbox://styles/the-city/cloq9dvvp00ey01qn5jkgcqzb";

/**
 * Since our custom style does not include links for map sprites, let's specify a fallback map style to load in:
 */
const FALLBACK_STYLE_URL_ROOT =
  "https://tiles.basemaps.cartocdn.com/gl/positron-gl-style/sprite@2x";

export const transformStyleRequest = (url: string, resourceType: string) => {
  if (resourceType === "SpriteImage") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.png`,
    };
  }
  if (resourceType === "SpriteJSON") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.json`,
    };
  }
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, MAPBOX_TOKEN);
  }
  return { url };
};

/**
 * When each paragraph gets within this distance (in px) of the top of the viewport, animations are triggered.
 */
const ANIMATION_TRIGGER_BUFFER = 200;

/**
 * Total length of time map animations take, in milliseconds.
 */
const ANIMATION_DURATION = 1500;

/**
 * A "blank" set of x and y scroll coordinates to initialize our react states with.
 * The values here are meaningless, just making sure the y coordinate is larger than our trigger buffer.
 */
const BLANK_SCROLL_POSITION = {
  x: 0,
  y: ANIMATION_TRIGGER_BUFFER + 100,
};

const superfundPointsData = {
  type: "FeatureCollection",
  name: "superfund_sites",
  crs: { type: "name", properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" } },
  features: [
    {
      type: "Feature",
      properties: { name: "Newtown Creek", id: "newtownCreek" },
      geometry: {
        type: "Point",
        coordinates: [-73.95440160941071, 40.739306591005246],
      },
    },
    {
      type: "Feature",
      properties: { name: "Meeker Avenue Plume", id: "meekerPlume" },
      geometry: {
        type: "Point",
        coordinates: [-73.93509046890642, 40.72650138797143],
      },
    },
    {
      type: "Feature",
      properties: { name: "Wolff-Alport Chemical Company", id: "wolffAlport" },
      geometry: {
        type: "Point",
        coordinates: [-73.90229984316227, 40.69247864681451],
      },
    },
    {
      type: "Feature",
      properties: { name: "Gowanus Canal", id: "gowanusCanal" },
      geometry: {
        type: "Point",
        coordinates: [-73.99087071923277, 40.6756316236844],
      },
    },
  ],
};

export const MapSequence: React.FC = () => {
  const mapRef = useRef<MapRef>();
  const mapContainerRef = useRef(createRef());

  /**
   * The scroll positions of each of the paragraphs overlayed on the map:
   */
  const [mapPosition, setMapPosition] = useState(BLANK_SCROLL_POSITION);

  /**
   * The number of the current slide the user is looking at, starting with 0:
   */
  const [currentSlide, setCurrentSlide] = useState(0);

  /**
   * Which point on the map the user is hovering over
   */
  const [hoverPoint, setHoverPoint] = useState("");

  useScrollPosition(
    ({ currPos }) => {
      setMapPosition(currPos);
    },
    [],
    //@ts-ignore
    mapContainerRef
  );

  /**
   * This hook determines which "current slide" the user is viewing by
   * looking at the scroll position of the map:
   */
  useEffect(() => {
    const currentSlide = mapPosition.y < ANIMATION_TRIGGER_BUFFER ? 1 : 0;
    setCurrentSlide(currentSlide);
  }, [mapPosition]);

  /**
   * This hook triggers custom animations based on when the current slide changes:
   */
  useEffect(() => {
    if (currentSlide === 0) {
      mapRef.current?.zoomTo(10, { duration: ANIMATION_DURATION });
    }
    // Animate zoom level change when the first paragraph scrolls by:
    if (currentSlide === 1) {
      mapRef.current?.fitBounds(
        // Bounding box around all superfund sites:
        [-74.014378, 40.656318, -73.879308, 40.751873],
        {
          duration: ANIMATION_DURATION,
        }
      );
    }
  }, [currentSlide]);

  const onHover = useCallback((event: mapboxgl.MapLayerMouseEvent) => {
    const site = event.features && event.features[0];
    const siteName = site?.properties?.name || "";
    setHoverPoint(siteName);
  }, []);

  const onClick = useCallback((event: mapboxgl.MapLayerMouseEvent) => {
    const site = event.features && event.features[0];
    const siteId = site?.properties?.id;
    if (!!siteId) {
      window.open(
        LINKS_TO_CONTENT.stories[siteId],
        "_blank",
        "noopener, noreferrer"
      );
    }
  }, []);

  const onMouseLeave = useCallback(() => setHoverPoint(""), []);

  return (
    <div
      style={{
        height: `100vh`,
      }}
    >
      <div
        className="map-container is-flex is-flex-direction-column is-align-items-center is-justify-content-center"
        //@ts-ignore
        ref={mapContainerRef}
      >
        <Map
          //@ts-ignore
          ref={mapRef}
          mapLib={maplibregl}
          initialViewState={{
            longitude: -73.977708344928,
            latitude: 40.713323256573386,
            zoom: 10,
          }}
          mapStyle={MAPBOX_STYLE_URL}
          transformRequest={transformStyleRequest}
          mapboxAccessToken={MAPBOX_TOKEN}
          onMouseMove={onHover}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          interactiveLayerIds={["superfund-sites"]}
          cursor={!!hoverPoint ? "pointer" : "auto"}
          scrollZoom={false}
          dragRotate={false}
        >
          <Source
            id="superfund-site-data"
            type="geojson"
            data={superfundPointsData as any}
          >
            <Layer
              id="superfund-sites"
              type="circle"
              source="superfund-site-data"
              paint={{
                "circle-color": "#fdbe2b",
                "circle-radius": [
                  "case",
                  ["==", ["get", "name"], hoverPoint || ""],
                  20,
                  15,
                ],
                "circle-opacity": [
                  "case",
                  ["==", ["get", "name"], hoverPoint || ""],
                  0.9,
                  0.6,
                ],
                "circle-radius-transition": {
                  duration: 1500,
                },
              }}
            />
            <Layer
              id="superfund-labels"
              type="symbol"
              source="superfund-site-data"
              layout={{
                "text-field": ["get", "name"],
                "text-variable-anchor": ["top", "bottom", "left", "right"],
                "text-radial-offset": 1.2,
                "text-justify": "auto",
                "icon-image": ["get", "icon"],
              }}
              paint={{
                "text-color": "#efefef",
              }}
            />
          </Source>
        </Map>
      </div>
    </div>
  );
};
